import { ActionReducerMap } from '@ngrx/store';

import { studyReducer, StudyState } from './study/study.reducer';
import { StudyEffects } from './study/study.effects';
import { ReleaseNoteEffects } from './release-note/release-note.effects';
import { releaseNoteReducer, ReleaseNoteState } from './release-note/release-note.reducer';
import { statisticsReducer, StatisticsState } from './statistics/statistics.reducer';
import { StatisticsEffects } from './statistics/statistics.effects';
import { researcherRequestReducer, ResearcherRequestState } from './requests/requests.reducer';
import { RequestsEffects } from './requests/requests.effects';
import { uploadReducer, UploadState } from './upload/upload.reducer';
import { UploadEffects } from './upload/upload.effects';
import { umResearcherReducer, UmResearcherState } from './um-researcher/um-researcher.reducer';
import { UmResearcherEffects } from './um-researcher/um-researcher.effects';
import { researcherStudyReducer, ResearcherStudyState } from './um-study/um-study.reducer';
import { ResearcherStudyEffects } from './um-study/um-study.effects';
import { interventionReducer, InterventionState } from './intervention/intervention.reducer';
import { InterventionEffects } from './intervention/intervention.effects';
import { SubscriptionEffects } from './subscription/subscription.effects';
import { subscriptionReducer, SubscriptionState } from './subscription/subscription.reducer';
import { InformativePageEffects } from './informative-page/informative-page.effects';
import { informativePageReducer, InformativePageState } from './informative-page/informative-page.reducer';
import { ModuleEffects } from './module/module.effects';
import { moduleReducer, ModuleState } from './module/module.reducer';
import { studyResearcherReducer, StudyResearcherState } from './study-researcher/study-researcher.reducer';
import { StudyResearcherEffects } from './study-researcher/study-researcher.effects';
import { DashboardEffects } from './dashboard.effects';
import { deviceReducer, DeviceState } from './device/device.reducer';
import { DeviceEffects } from './device/device.effects';
import { InterventionContentItemEffects } from './intervention-content-item/intervention-content-item.effects';
import {
  interventionContentItemReducer,
  InterventionContentItemState,
} from './intervention-content-item/intervention-content-item.reducer';
import { ModuleItemEffects } from './module-item/module-item.effects';
import { moduleItemReducer, ModuleItemState } from './module-item/module-item.reducer';
import { CalculatedVariableEffects } from './calculated-variable/calculated-variable.effect';
import { calculatedVariableReducer, CalculatedVariableState } from './calculated-variable/calculated-variable.reducer';
import { TiimStudyParticipantEffects } from './study-participant/tiim-study-participant/tiim-study-participant.effects';
import {
  tiimStudyParticipantReducer,
  TiimStudyParticipantState,
} from './study-participant/tiim-study-participant/tiim-study-participant.reducer';
import {
  ccStudyParticipantReducer,
  CcStudyParticipantState,
} from './study-participant/cc-study-participant/cc-study-participant.reducer';
import { CcStudyParticipantEffects } from './study-participant/cc-study-participant/cc-study-participant.effects';
import { TemplateEffects } from './templates/templates.effects';
import { TemplateReducer, TemplateState } from './templates/templates.reducer';
import { SectionsEffects } from './sections/sections.effects';
import { SectionReducer, SectionState } from './sections/sections.reducer';
import { SubsectionEffects } from './subsections/subsections.effects';
import { SubsectionReducer, SubsectionState } from './subsections/subsections.reducer';
import { BarEffects } from './bars/bars.effects';
import { BarReducer, BarState } from './bars/bars.reducer';
import { TemplateConfigEffects } from './template-config/template-config.effects';
import { TemplateConfigReducer, TemplateConfigState } from './template-config/template-config.reducer';
import { TimingRulesReducer, TimingRulesState } from './timing-rules/timing-rules.reducer';
import { TimingRulesEffects } from './timing-rules/timing-rules.effects';
import { MetricsReducer, MetricsState } from './metrics/metrics.reducer';
import { MetricsEffects } from './metrics/metrics.effects';
import { CategoryReducer, CategoryState } from './category/category.reducer';
import { CategoryEffects } from './category/category.effects';
import { umParticipantReducer, UmParticipantState } from './um-participant/um-participant.reducer';
import { UmParticipantEffects } from './um-participant/um-participant.effects';

export interface DashboardState {
  study: StudyState;
  releaseNotes: ReleaseNoteState;
  statistics: StatisticsState;
  researcherRequests: ResearcherRequestState;
  upload: UploadState;
  umResearcher: UmResearcherState;
  umParticipant: UmParticipantState;
  researcherStudies: ResearcherStudyState;
  intervention: InterventionState;
  subscription: SubscriptionState;
  informativePage: InformativePageState;
  studyResearcher: StudyResearcherState;
  device: DeviceState;
  modules: ModuleState;
  interventionContentItem: InterventionContentItemState;
  moduleItem: ModuleItemState;
  calculatedVariable: CalculatedVariableState;
  tiimStudyParticipant: TiimStudyParticipantState;
  ccStudyParticipant: CcStudyParticipantState;
  template: TemplateState;
  section: SectionState;
  subsection: SubsectionState;
  bar: BarState;
  templateConfig: TemplateConfigState;
  timingRules: TimingRulesState;
  metrics: MetricsState;
  category: CategoryState;
}

export const dashboardReducers: ActionReducerMap<DashboardState> = {
  study: studyReducer,
  releaseNotes: releaseNoteReducer,
  statistics: statisticsReducer,
  researcherRequests: researcherRequestReducer,
  umResearcher: umResearcherReducer,
  umParticipant: umParticipantReducer,
  researcherStudies: researcherStudyReducer,
  upload: uploadReducer,
  intervention: interventionReducer,
  subscription: subscriptionReducer,
  informativePage: informativePageReducer,
  studyResearcher: studyResearcherReducer,
  device: deviceReducer,
  modules: moduleReducer,
  interventionContentItem: interventionContentItemReducer,
  moduleItem: moduleItemReducer,
  calculatedVariable: calculatedVariableReducer,
  tiimStudyParticipant: tiimStudyParticipantReducer,
  ccStudyParticipant: ccStudyParticipantReducer,
  template: TemplateReducer,
  section: SectionReducer,
  subsection: SubsectionReducer,
  bar: BarReducer,
  templateConfig: TemplateConfigReducer,
  timingRules: TimingRulesReducer,
  metrics: MetricsReducer,
  category: CategoryReducer,
};

export const dashboardEffects = [
  DashboardEffects,
  StudyEffects,
  ReleaseNoteEffects,
  StatisticsEffects,
  RequestsEffects,
  UmResearcherEffects,
  UmParticipantEffects,
  ResearcherStudyEffects,
  UploadEffects,
  InterventionEffects,
  SubscriptionEffects,
  InformativePageEffects,
  StudyResearcherEffects,
  DeviceEffects,
  ModuleEffects,
  StudyResearcherEffects,
  InterventionContentItemEffects,
  ModuleItemEffects,
  CalculatedVariableEffects,
  TiimStudyParticipantEffects,
  CcStudyParticipantEffects,
  CcStudyParticipantEffects,
  TemplateEffects,
  SectionsEffects,
  SubsectionEffects,
  BarEffects,
  TemplateConfigEffects,
  TimingRulesEffects,
  MetricsEffects,
  CategoryEffects,
];
